import React, { useCallback, useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { cn } from '../index';

function readIsDark(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem('theme');
  // Explicit choice only — default is light (matches index.html script)
  return stored === 'dark';
}

function applyTheme(dark: boolean) {
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
  localStorage.setItem('theme', dark ? 'dark' : 'light');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? '#111827' : '#f4f6fa');
}

export const ThemeToggle: React.FC<{ className?: string }> = ({ className }) => {
  const [dark, setDark] = useState(readIsDark);

  useEffect(() => {
    // Sync from DOM in case the anti-flash script already set class
    setDark(document.documentElement.classList.contains('dark'));
  }, []);

  const toggle = useCallback(() => {
    setDark((prev) => {
      const next = !prev;
      applyTheme(next);
      return next;
    });
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--fg-muted)]',
        'hover:bg-[var(--muted)] hover:text-[var(--fg)] transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
        className,
      )}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
};
